import * as React from 'react'
import { Car, Loader2, Pencil } from 'lucide-react'
import { Link } from '@tanstack/react-router'

import type { TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { applyTaxRateToLineItem, calculateLineTotalCents } from '@/lib/line-item-calculations'
import {
  getTransportationLineItem,
  isTransportationLineItem,
  replaceTransportationLineItem,
  withoutTransportationLineItems,
} from '@/lib/transportation-line-items'
import { formatCurrency } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'
import { useTransportationItem } from '@/stores/transportation.store'
import { SafeHtml } from '@/components/shared/safe-html'
import {
  TransportationMileageDialog,
  type TransportationMileageEvent,
} from '@/components/shared/transportation-mileage-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import {
  QUOTE_ITEM_NO_TAX_VALUE,
  QuoteItemDerivedQuantity,
  QuoteItemInlineSelect,
  QuoteItemList,
  QuoteItemListContainer,
  QuoteItemTwoLineRow,
  buildQuoteItemTaxOptions,
  formatQuoteItemTaxSubLine,
  formatQuoteItemUnitLabel,
} from './quote-item-table'
import { QuoteLineItemEditSheet } from './quote-line-item-edit-sheet'
import type { QuoteLineItem } from './quote-line-items-table'
import { useAutoTransportationMileage } from './use-auto-transportation-mileage'

type TransportationLineItemSectionProps = {
  documentKey: string
  organizationId: string
  events: TransportationMileageEvent[]
  items: QuoteLineItem[]
  taxRates?: TaxRate[]
  autoApplyTaxRateId?: string
  disabled?: boolean
  applyOrganizationDefault?: boolean
  /** Renders without its own divider/description when hosted inside a labelled sub-section. */
  embedded?: boolean
  /** Fired when the row's tax picker diverges from an engaged auto-apply rate. */
  onAutoApplyOverride?: () => void
  onItemsChange: (items: QuoteLineItem[]) => void
}

function formatMiles(miles: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(miles)
}

export function TransportationLineItemSection({
  documentKey,
  organizationId,
  events,
  items,
  taxRates = [],
  autoApplyTaxRateId,
  disabled = false,
  applyOrganizationDefault = false,
  embedded = false,
  onAutoApplyOverride,
  onItemsChange,
}: TransportationLineItemSectionProps) {
  const { item: transportationItem, isLoading: transportationItemLoading } = useTransportationItem(organizationId)
  const checkboxId = React.useId()
  const [enabledOverride, setEnabledOverride] = React.useState<boolean>()
  const [editing, setEditing] = React.useState(false)
  const transportationLines = items.filter(isTransportationLineItem)
  const transportationLine = getTransportationLineItem(items)

  React.useEffect(() => {
    setEnabledOverride(undefined)
    setEditing(false)
  }, [documentKey])

  const enabled = enabledOverride ?? Boolean(
    transportationLine || (applyOrganizationDefault && transportationItem?.alwaysInclude),
  )
  const {
    autoCalculation,
    destinationCount,
  } = useAutoTransportationMileage({
    documentKey,
    organizationId,
    events,
    transportationItem,
    items,
    taxRates,
    autoApplyTaxRateId,
    enabledByDefault: enabledOverride === undefined
      && applyOrganizationDefault
      && Boolean(transportationItem?.alwaysInclude),
    hasTransportationLine: Boolean(transportationLine),
    disabled,
    onItemsChange,
  })
  const isAutoCalculating = autoCalculation?.status === 'calculating'

  const handleEnabledChange = (checked: boolean | 'indeterminate') => {
    const nextEnabled = checked === true
    setEnabledOverride(nextEnabled)
    if (!nextEnabled && transportationLines.length > 0) {
      onItemsChange(withoutTransportationLineItems(items))
    }
  }

  const handleSave = (updatedItem: QuoteLineItem) => {
    onItemsChange(replaceTransportationLineItem(items, updatedItem))
  }

  const handleTaxRateChange = (taxRateId: string | undefined) => {
    if (!transportationLine) return
    // Same divergence contract the line items table honours: picking anything
    // other than the engaged rate retires the "apply to all" state.
    if (autoApplyTaxRateId && taxRateId !== autoApplyTaxRateId) {
      onAutoApplyOverride?.()
    }
    const selectedRate = taxRateId ? taxRates.find((rate) => rate.id === taxRateId) : undefined
    onItemsChange(replaceTransportationLineItem(
      items,
      applyTaxRateToLineItem(transportationLine, selectedRate),
    ))
  }

  const lineTotalCents = transportationLine
    ? calculateLineTotalCents(transportationLine.quantity, transportationLine.unitPriceCents)
    : 0

  return (
    <section aria-labelledby={`${checkboxId}-heading`} className={embedded ? undefined : 'border-t pt-4'}>
      <div className={cn('space-y-4', !embedded && 'rounded-lg border bg-muted/20 p-4')}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <Checkbox
              id={checkboxId}
              checked={enabled}
              onCheckedChange={handleEnabledChange}
              disabled={disabled || transportationItemLoading || !transportationItem}
              className="mt-1"
            />
            <div className="min-w-0 space-y-1">
              <Label id={`${checkboxId}-heading`} htmlFor={checkboxId} className="flex cursor-pointer items-center gap-2 font-medium">
                <Car className="size-4" aria-hidden="true" />
                Transportation
              </Label>
              {!embedded && (
                <p className="text-sm text-muted-foreground">
                  Calculate a mileage charge separately from your other primary line items.
                </p>
              )}
            </div>
          </div>

          {enabled && transportationItem ? (
            <TransportationMileageDialog
              organizationId={organizationId}
              events={events}
              items={items}
              taxRates={taxRates}
              autoApplyTaxRateId={autoApplyTaxRateId}
              disabled={disabled || isAutoCalculating}
              triggerLabel={transportationLine ? 'Recalculate mileage' : 'Calculate mileage'}
              onItemsChange={(nextItems) => {
                setEnabledOverride(true)
                onItemsChange(nextItems)
              }}
            />
          ) : null}
        </div>

        {!transportationItemLoading && !transportationItem ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-dashed bg-background p-3 text-sm">
            <span className="text-muted-foreground">Configure a mileage rate before enabling Transportation.</span>
            <Button asChild type="button" size="sm" variant="outline">
              <Link to="/services" search={{ section: 'catalog' }}>Configure Transportation</Link>
            </Button>
          </div>
        ) : enabled && transportationLine ? (
          embedded ? (
            <QuoteItemListContainer>
              <QuoteItemList>
                <li>
                  <QuoteItemTwoLineRow
                    name={transportationLine.name}
                    totalLabel={`$${formatCurrency(lineTotalCents)}`}
                    // Mileage is derived from the calculated route, not stepped by hand.
                    quantityControl={
                      <QuoteItemDerivedQuantity label={formatMiles(transportationLine.quantity)} />
                    }
                    metaItems={[
                      // Unit is fixed at per-mile for a mileage charge.
                      formatQuoteItemUnitLabel(transportationLine.unit),
                      <QuoteItemInlineSelect
                        key="tax"
                        label={`Change tax rate for ${transportationLine.name}`}
                        triggerLabel={formatQuoteItemTaxSubLine(transportationLine)}
                        value={transportationLine.isTaxable && transportationLine.salesTaxRateId
                          ? transportationLine.salesTaxRateId
                          : QUOTE_ITEM_NO_TAX_VALUE}
                        options={buildQuoteItemTaxOptions(taxRates)}
                        disabled={disabled}
                        onSelect={(value) => handleTaxRateChange(value === QUOTE_ITEM_NO_TAX_VALUE ? undefined : value)}
                      />,
                    ]}
                    priceControl={
                      <span className="text-xs tabular-nums text-muted-foreground">
                        ${formatCurrency(transportationLine.unitPriceCents)} per mile
                      </span>
                    }
                    menu={
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-7 shrink-0 text-muted-foreground"
                            aria-label={`Actions for ${transportationLine.name}`}
                            disabled={disabled}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setEditing(true)}>Edit</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    }
                  />
                </li>
              </QuoteItemList>
            </QuoteItemListContainer>
          ) : (
            <div className="rounded-md border bg-background p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{transportationLine.name}</p>
                  {transportationLine.description ? (
                    <SafeHtml html={transportationLine.description} className="mt-1 text-sm text-muted-foreground line-clamp-2" />
                  ) : null}
                  <p className="mt-2 text-sm text-muted-foreground">
                    {formatMiles(transportationLine.quantity)} mi × ${formatCurrency(transportationLine.unitPriceCents)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-financial font-semibold tabular-nums">${formatCurrency(lineTotalCents)}</span>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)} disabled={disabled}>
                    <Pencil className="size-4" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          )
        ) : enabled && autoCalculation?.status === 'calculating' ? (
          <div className="flex items-center gap-2 rounded-md border border-dashed bg-background p-3 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Calculating round-trip mileage…
          </div>
        ) : enabled && autoCalculation?.status === 'within-threshold' ? (
          <p className="rounded-md border border-dashed bg-background p-3 text-sm text-muted-foreground">
            The {formatMiles(autoCalculation.totalMiles)}-mile round trip is within your {formatMiles(autoCalculation.thresholdMiles)}-mile free threshold. No charge was added.
          </p>
        ) : enabled && autoCalculation?.status === 'error' ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            Automatic round-trip calculation failed: {autoCalculation.message}
          </p>
        ) : enabled ? (
          <p className="rounded-md border border-dashed bg-background p-3 text-sm text-muted-foreground">
            {destinationCount === 0
              ? 'Add an event location to calculate Transportation mileage.'
              : 'Calculate mileage to add the Transportation line item. If the trip is within the free threshold, no charge is added.'}
          </p>
        ) : null}
      </div>

      <QuoteLineItemEditSheet
        item={transportationLine ?? null}
        availableItems={[]}
        availableTaxRates={taxRates}
        open={editing && !!transportationLine}
        onOpenChange={setEditing}
        onSave={handleSave}
        disabled={disabled}
      />
    </section>
  )
}
